"use strict";
/**
 * Copyright © 2020 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.asAccessibleSubMenu = exports.asAccessibleMenu = exports.asRelative = exports.asStylableList = exports.asSimpleSubMenu = exports.withBaseMenuStyles = exports.withBaseSubMenuStyles = void 0;

var react_1 = __importDefault(require("react"));

var lodash_1 = require("lodash");

var core_1 = require("@bodiless/core");

var fclasses_1 = require("@bodiless/fclasses");

var components_1 = require("@bodiless/components");

var withMenuContext_1 = require("./withMenuContext");
/*
 * Utility Styles
 * ===========================================
 */


var isContextActive = function isContextActive() {
  var _a = core_1.useEditContext(),
      isActive = _a.isActive,
      isEdit = _a.isEdit;

  return isEdit && isActive;
};

var asVerticalSubMenu = fclasses_1.withDesign({
  Wrapper: fclasses_1.withDesign({
    List: fclasses_1.addClasses('flex-col')
  })
});
var asRelative = fclasses_1.withDesign({
  Wrapper: fclasses_1.withDesign({
    WrapperItem: fclasses_1.addClasses('relative')
  })
});
exports.asRelative = asRelative;
var asExpandedOnActive = fclasses_1.withDesign({
  Wrapper: fclasses_1.withDesign({
    WrapperItem: fclasses_1.addClassesIf(isContextActive)('overflow-visible')
  })
});
var asResponsiveSublist = fclasses_1.withDesign({
  Wrapper: fclasses_1.withDesign({
    List: fclasses_1.addClasses('w-content min-w-full')
  })
});
var asStylableList = fclasses_1.withDesign({
  Wrapper: fclasses_1.stylable,
  Item: fclasses_1.stylable,
  Title: fclasses_1.stylable
});
exports.asStylableList = asStylableList;

var asNav = function asNav(Component) {
  var Nav = function Nav(props) {
    var _a = props["aria-label"],
        ariaLabel = _a === void 0 ? 'Navigation Menu' : _a;
    return react_1.default.createElement("nav", {
      "aria-label": ariaLabel
    }, react_1.default.createElement(Component, __assign({}, props)));
  };

  return Nav;
};
/**
 * Accessibility Features
 * ===========================================
 */


var asAccessibleMenu = fclasses_1.withDesign({
  Wrapper: lodash_1.flow(asNav, fclasses_1.addProps({
    role: 'menubar',
    'aria-label': 'Navigation Menu'
  })),
  Item: fclasses_1.addProps({
    tabIndex: 0,
    role: 'menuitem'
  })
});
exports.asAccessibleMenu = asAccessibleMenu;
var asAccessibleSubMenu = fclasses_1.withDesign({
  Wrapper: fclasses_1.withDesign({
    WrapperItem: fclasses_1.addProps({
      'aria-haspopup': true
    }),
    List: fclasses_1.addProps({
      role: 'menu',
      'aria-label': 'Navigation Sub Menu'
    })
  }),
  Item: fclasses_1.addProps({
    role: 'none'
  }),
  Title: fclasses_1.addProps({
    role: 'menuitem'
  })
});
exports.asAccessibleSubMenu = asAccessibleSubMenu;
var asAccessibleSimpleMenu = lodash_1.flow(components_1.withSubListDesign(1)({
  SubMenu: asAccessibleSubMenu
}), asAccessibleMenu);
/*
 * Base Menu Styles
 * ===========================================
 */

var withHoverStyles = fclasses_1.withDesign({
  Item: lodash_1.flow(fclasses_1.addClasses('hover:overflow-visible focus:overflow-visible'), fclasses_1.removeClassesIf(withMenuContext_1.useIsMenuOpen)('hover:overflow-visible focus:overflow-visible'))
});
var withBaseMenuStyles = lodash_1.flow(withHoverStyles, fclasses_1.withDesign({
  Wrapper: fclasses_1.addClasses('relative flex'),
  Item: fclasses_1.addClasses('overflow-hidden')
}));
exports.withBaseMenuStyles = withBaseMenuStyles;
/*
 * Base Sub Menu Styles
 * ===========================================
 */

var withBaseSubMenuStyles = fclasses_1.withDesign({
  Wrapper: fclasses_1.withDesign({
    List: fclasses_1.addClasses('flex absolute left-0')
  })
});
exports.withBaseSubMenuStyles = withBaseSubMenuStyles;
/*
 * Simple Sub Menu Styles
 * ===========================================
 */

var asSimpleSubMenu = lodash_1.flow(asResponsiveSublist, asVerticalSubMenu, withBaseSubMenuStyles, asExpandedOnActive, asRelative);
exports.asSimpleSubMenu = asSimpleSubMenu;
/*
 * Simple Menu Sub Menu Styles
 * ===========================================
 */

var asSimpleSubMenuStyles = fclasses_1.withDesign({
  SubMenu: asSimpleSubMenu
});
/*
 * Simple Menu Styles
 * ===========================================
 */

var asSimpleMenuTopNav = lodash_1.flow(fclasses_1.withDesign({
  Item: asSimpleSubMenuStyles
}), withBaseMenuStyles, asAccessibleSimpleMenu);
exports.default = asSimpleMenuTopNav;